import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useRequestAnalyticsQuery } from "@/hooks/useRequestAnalyticsQuery";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { AnalyticsCardData, DateRange } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatInteger } from "@/utils/number";

export const useTotalRequests = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const [rawData, setRawData] = useState<{ totalRequests: number; totalValue: number } | null>(
    null,
  );
  const { baseParams, beginRequest, requestKey } = useRequestAnalyticsQuery({
    stores,
    dateRange,
    deviceType,
    areaIds,
  });
  const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(stores);

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!baseParams || !requestKey) {
      setRawData(null);
      return;
    }

    const fetchTotalRequests = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const response = await analyticsService.getRequestAnalytics(baseParams);
        const totalRequests = response?.data?.totalRequests ?? 0;
        const totalValue = Math.round(response?.data?.totalRequestValue ?? 0);

        if (totalRequests <= 0) {
          if (isCurrent()) setRawData(null);
          return;
        }
        if (isCurrent()) setRawData({ totalRequests, totalValue });
      } catch (error) {
        console.error("Error fetching total requests:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    void fetchTotalRequests();
  }, [baseParams, beginRequest, requestKey]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: null,
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    const value = formatCurrencyWithNoDecimals(
      rawData.totalValue,
      currencyCode as CurrencyCode,
      currencySymbol,
    );

    return {
      previousValue: null,
      value: formatInteger(rawData.totalRequests, currentLanguage),
      subtitle: i18n.t("common.analytics.itemRequestsValue", { value }),
      isPositive: true,
      pillText: i18n.t("common.analytics.requestsCount", { count: rawData.totalRequests }),
      rawValue: rawData.totalRequests,
    };
  }, [rawData, currentLanguage, currencyCode, currencySymbol]);
};
